import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
} from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";

const BLUE = "#1565C0";
const BLUE_DARK = "#0D47A1";
const TEXT = "#0f172a";
const MUTED = "#64748b";
const BORDER = "#e2e8f0";
const SOFT = "#f8fafc";

const money = (value) => {
  const n = Number(value || 0);
  return `$${n.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatShortDate = (raw) => {
  if (!raw) return "";

  const value = String(raw);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
  });
};

const formatFullDate = (raw) => {
  if (!raw) return "";

  const value = String(raw);
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("es-EC", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const MetricBox = ({ title, value, helper, loading, main }) => {
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${BORDER}`,
        boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
        bgcolor: "#fff",
        height: "100%",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, mb: 0.8 }}>
        {title}
      </Typography>

      {loading ? (
        <CircularProgress size={22} />
      ) : (
        <Typography
          sx={{
            fontSize: { xs: 22, md: 27 },
            fontWeight: 800,
            color: main ? BLUE : TEXT,
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
      )}

      <Typography
        sx={{
          mt: 0.8,
          color: MUTED,
          fontSize: 12.5,
          lineHeight: 1.35,
          fontWeight: 500,
        }}
      >
        {helper}
      </Typography>
    </Paper>
  );
};

const Panel = ({ title, subtitle, children, minHeight }) => {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
        bgcolor: "#fff",
        height: "100%",
        minHeight: minHeight || "auto",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 19, color: TEXT }}>
            {title}
          </Typography>

          {subtitle ? (
            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.4 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      {children}
    </Paper>
  );
};

const EmptyState = ({ text = "Sin información suficiente para mostrar." }) => {
  return (
    <Box
      sx={{
        height: 210,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#94a3b8",
        fontSize: 14,
        fontWeight: 700,
        textAlign: "center",
      }}
    >
      {text}
    </Box>
  );
};

const LoadingBox = () => {
  return (
    <Box
      sx={{
        height: 210,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress size={30} />
    </Box>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <Paper
      sx={{
        p: 1.2,
        borderRadius: 2,
        border: `1px solid ${BORDER}`,
        boxShadow: "0 10px 30px rgba(15,23,42,0.14)",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: TEXT, mb: 0.7 }}>
        {label}
      </Typography>

      {payload.map((item) => (
        <Typography
          key={item.dataKey}
          sx={{ fontSize: 12, color: "#475569", fontWeight: 700 }}
        >
          {item.name}: {money(item.value)}
        </Typography>
      ))}
    </Paper>
  );
};

const DashboardTable = ({ columns, rows, emptyText, rowsPerPageDefault = 5 }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageDefault);

  const visibleRows = useMemo(() => {
    const start = page * rowsPerPage;
    return (rows || []).slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  if (!rows?.length) {
    return <EmptyState text={emptyText} />;
  }

  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{
                    bgcolor: BLUE,
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    borderBottom: `1px solid ${BLUE_DARK}`,
                    whiteSpace: "nowrap",
                    py: 1.15,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow key={row.id || row.cuenta_id || row.codigo || row.fecha || index} hover>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    sx={{
                      fontSize: 12.5,
                      fontWeight: col.bold ? 700 : 500,
                      color: col.muted ? MUTED : "#334155",
                      borderBottom: "1px solid #f1f5f9",
                      maxWidth: col.maxWidth || 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      py: 1.15,
                    }}
                  >
                    {col.render ? col.render(row, index) : row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={(rows || []).length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage="Filas por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        sx={{
          borderTop: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          "& .MuiTablePagination-toolbar": {
            px: 1,
            minHeight: 48,
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: 12.5,
            color: MUTED,
            fontWeight: 600,
          },
          "& .MuiTablePagination-select": {
            fontSize: 12.5,
            fontWeight: 700,
            color: TEXT,
          },
          "& .MuiIconButton-root": {
            color: BLUE,
          },
          "& .MuiIconButton-root:hover": {
            bgcolor: "rgba(21,101,192,0.08)",
          },
          "& .MuiIconButton-root.Mui-disabled": {
            color: "#cbd5e1",
          },
        }}
      />
    </Box>
  );
};

const Banks = ({ accessToken, periodo }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const endpoint = useMemo(() => {
    if (periodo === "mes") {
      return `/dashboard/bancos/?periodo=mes&mes=${getCurrentMonth()}`;
    }

    return `/dashboard/bancos/?periodo=${periodo || "mes"}`;
  }, [periodo]);

  useEffect(() => {
    const loadBanks = async () => {
      if (!accessToken) return;

      setLoading(true);
      setError("");

      try {
        const res = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const contentType = res.headers.get("content-type") || "";
        let json = null;

        if (contentType.includes("application/json")) {
          json = await res.json();
        } else {
          await res.text();
          throw new Error(`Respuesta no JSON desde ${endpoint}. Estado HTTP ${res.status}.`);
        }

        if (!res.ok) {
          throw new Error(json?.error || `Error HTTP ${res.status}`);
        }

        setData(json);
      } catch (err) {
        setData(null);
        setError(err.message || "No se pudo cargar el flujo de bancos.");
      } finally {
        setLoading(false);
      }
    };

    loadBanks();
  }, [accessToken, endpoint]);

  const totales = data?.totales || {};
  const cuentas = data?.cuentas || [];
  const movimientos = data?.movimientos_recientes || [];

  const flujoDiario = useMemo(() => {
    return (data?.flujo_diario || []).map((item) => ({
      ...item,
      label: formatShortDate(item.fecha),
      entradas: Number(item.entradas || 0),
      salidas: Number(item.salidas || 0),
      neto: Number(item.neto || 0),
    }));
  }, [data?.flujo_diario]);

  const cuentasChart = useMemo(() => {
    return cuentas.slice(0, 8).map((item) => ({
      cuenta: String(item.cuenta || "Banco").slice(0, 18),
      saldo_actual: Number(item.saldo_actual || 0),
      neto: Number(item.neto || 0),
    }));
  }, [cuentas]);

  return (
    <Box>
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
          mb: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 19, color: TEXT }}>
              Estado de bancos
            </Typography>

            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.4 }}>
              Entradas, salidas, flujo neto y saldo actual de cuentas bancarias
            </Typography>
          </Box>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricBox
            title="Entradas bancarias"
            value={money(totales.entradas)}
            helper={`${Number(totales.cuentas || 0)} cuentas bancarias`}
            loading={loading}
            main
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricBox
            title="Salidas bancarias"
            value={money(totales.salidas)}
            helper="Pagos y egresos registrados"
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricBox
            title="Flujo neto bancos"
            value={money(totales.neto)}
            helper="Entradas menos salidas del período"
            loading={loading}
            main
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricBox
            title="Saldo actual bancos"
            value={money(totales.saldo_actual)}
            helper="Saldo contable acumulado"
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} lg={8}>
          <Panel
            title="Flujo bancario"
            subtitle="Evolución diaria de entradas, salidas y neto"
            minHeight={390}
          >
            {loading ? (
              <LoadingBox />
            ) : flujoDiario.length ? (
              <Box sx={{ width: "100%", height: 310 }}>
                <ResponsiveContainer>
                  <AreaChart data={flujoDiario} margin={{ top: 10, right: 18, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="entradas" name="Entradas" stroke={BLUE} fill={BLUE} fillOpacity={0.16} strokeWidth={2.6} />
                    <Area type="monotone" dataKey="salidas" name="Salidas" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.16} strokeWidth={2.3} />
                    <Area type="monotone" dataKey="neto" name="Neto" stroke={TEXT} fill={TEXT} fillOpacity={0.08} strokeWidth={2.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState text="No hay flujo bancario en este período." />
            )}
          </Panel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Panel
            title="Saldos por banco"
            subtitle="Saldo actual y flujo neto por cuenta"
            minHeight={390}
          >
            {loading ? (
              <LoadingBox />
            ) : cuentasChart.length ? (
              <Box sx={{ width: "100%", height: 310 }}>
                <ResponsiveContainer>
                  <BarChart data={cuentasChart} layout="vertical" margin={{ top: 6, right: 18, left: 24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                    <YAxis dataKey="cuenta" type="category" width={110} tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="saldo_actual" name="Saldo actual" fill={BLUE} radius={[0, 8, 8, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState text="No hay cuentas bancarias activas." />
            )}
          </Panel>
        </Grid>

        <Grid item xs={12}>
          <Panel
            title="Resumen por cuenta bancaria"
            subtitle="Entradas, salidas, neto y saldo actual"
          >
            {loading ? (
              <LoadingBox />
            ) : (
              <DashboardTable
                emptyText="No hay cuentas bancarias para mostrar."
                rows={cuentas}
                rowsPerPageDefault={5}
                columns={[
                  {
                    key: "codigo",
                    label: "Código",
                    bold: true,
                    maxWidth: 120,
                  },
                  {
                    key: "cuenta",
                    label: "Cuenta",
                    bold: true,
                    maxWidth: 260,
                  },
                  {
                    key: "entradas",
                    label: "Entradas",
                    align: "right",
                    render: (row) => money(row.entradas),
                  },
                  {
                    key: "salidas",
                    label: "Salidas",
                    align: "right",
                    render: (row) => money(row.salidas),
                  },
                  {
                    key: "neto",
                    label: "Neto",
                    align: "right",
                    render: (row) => money(row.neto),
                  },
                  {
                    key: "saldo_actual",
                    label: "Saldo actual",
                    align: "right",
                    render: (row) => money(row.saldo_actual),
                  },
                ]}
              />
            )}
          </Panel>
        </Grid>

        <Grid item xs={12}>
          <Panel
            title="Movimientos bancarios recientes"
            subtitle="Últimos cobros y pagos registrados en cuentas bancarias"
          >
            {loading ? (
              <LoadingBox />
            ) : (
              <DashboardTable
                emptyText="No hay movimientos bancarios registrados en este período."
                rows={movimientos}
                rowsPerPageDefault={10}
                columns={[
                  {
                    key: "fecha",
                    label: "Fecha",
                    maxWidth: 150,
                    render: (row) => formatFullDate(row.fecha),
                  },
                  {
                    key: "tipo",
                    label: "Tipo",
                    bold: true,
                    maxWidth: 90,
                  },
                  {
                    key: "cuenta",
                    label: "Cuenta",
                    maxWidth: 220,
                  },
                  {
                    key: "persona",
                    label: "Persona",
                    maxWidth: 240,
                  },
                  {
                    key: "comprobante",
                    label: "Comprobante",
                    maxWidth: 160,
                  },
                  {
                    key: "entrada",
                    label: "Entrada",
                    align: "right",
                    render: (row) => money(row.entrada),
                  },
                  {
                    key: "salida",
                    label: "Salida",
                    align: "right",
                    render: (row) => money(row.salida),
                  },
                ]}
              />
            )}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Banks;